import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GridToolbar } from '@mui/x-data-grid-pro';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { getItemColumns } from '../TableDef';
import { callAPI } from 'shared/api/callApi';
import CustomSkeleton from 'shared/ui/Skeleton';
import ThemeButton from 'shared/ui/Button';

import ItemDetail from './ItemDetail';
import ItemAdd from './ItemAdd';
import { MESSAGES } from '../../../lib/configUtils';

export default function ItemList({
    userEmail,
    getAccessTokenSilently,
    loadingState,
    setLoadingState,
    itemFormulasRows,
}) {
    const dispatch = useDispatch();

    const [itemRows, setItemRows] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);

    const handleDetailOpen = (rowId) => {
        setDetailOpen(true);
        setSelectedRowId(rowId);
    };

    const handleDelete = (rowId) => {
        // 삭제는 서버에서 이 상품을 쓰는 신고 상세·Jinro 요약까지 함께 지우므로, 범위를 알리고 확인받는다.
        const isConfirmed = window.confirm(
            'Deleting this item also deletes all duty entry details and Jinro summary data that use it. Are you sure?',
        );
        if (!isConfirmed) return;

        callAPI({
            method: 'delete',
            module: 'duty_calculator',
            component: 'duty_item',
            getAccessTokenSilently,
            email: userEmail,
            obj: { id: rowId },
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: false }));
            },
            onSuccess: (resp) => {
                console.log('item delete response', resp);
                setItemRows(itemRows.filter((row) => row.id !== rowId));
                dispatch(
                    setSnackbar({
                        open: true,
                        message: MESSAGES.DELETE_SUCCESS,
                        severity: 'success',
                    }),
                );
            },
            onError: () => {
                dispatch(setSnackbar({ open: true, message: MESSAGES.DELETE_ERROR, severity: 'warning' }));
            },
        });
    };

    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'duty_items',
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, itemList: true }));
            },
            onSuccess: (resp) => {
                console.log('item list response', resp);
                setItemRows(resp?.data ?? []);
                setLoadingState((prev) => ({ ...prev, itemList: false }));
            },
            onError: (error) => {
                dispatch(setSnackbar({ open: true, message: error.message, severity: 'error' }));
            },
        });
    }, []);

    if (loadingState.itemList) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <CustomSkeleton />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '92%', p: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.secondary' }}>Item List</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1.5 }}>
                <ThemeButton text="Add Item" onClick={() => setAddOpen(true)} />
            </Box>
            <DataGridPremium
                rows={itemRows}
                columns={getItemColumns({ onView: handleDetailOpen, onDelete: handleDelete })}
                density="compact"
                disableColumnFilter
                disableAggregation
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        printOptions: { disableToolbarButton: true },
                        excelOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                    },
                }}
                disableRowSelectionOnClick
                cellSelection
                sx={{ width: '100%' }}
            />
            <ItemDetail
                newOpen={detailOpen}
                setNewOpen={setDetailOpen}
                selectedRowId={selectedRowId}
                itemRows={itemRows}
                setItemRows={setItemRows}
                loadingState={loadingState}
                itemFormulasRows={itemFormulasRows}
                setLoadingState={setLoadingState}
            />
            <ItemAdd
                newOpen={addOpen}
                setNewOpen={setAddOpen}
                itemRows={itemRows}
                setItemRows={setItemRows}
                loadingState={loadingState}
                itemFormulasRows={itemFormulasRows}
                userEmail={userEmail}
                getAccessTokenSilently={getAccessTokenSilently}
                setLoadingState={setLoadingState}
            />
        </Box>
    );
}
